<template>
  <section>
    <errors-list :errorMessage="errorMessage" />

    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :total-items="pagination.totalItems"
      :pagination.sync="pagination"
      item-key="_id"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <template :header="header" :row="props.item" v-for="(header, index) in translatedHeaders">
            <td v-if="header.value === 'message' && getEventLink(props.item)" :key="index">
              {{ props.item.message }}
              <a :href="getEventLink(props.item)" target="_blank">(view)</a>
            </td>
            <td
              v-else-if="
                header.value === 'message' &&
                  (props.item.meta.smsQueueId ||
                    (props.item.meta.messageId && props.item.type.substring(0, 3) === 'Sms'))
              "
              :key="index"
            >
              {{ props.item.message }}
              <hidden-phone-number :id="props.item.meta.smsQueueId || props.item.meta.messageId" />
            </td>
            <td v-else-if="header.value === 'meta.employeeName'" :key="index">
              <span v-if="props.item.meta.employeeName">{{ props.item.meta.employeeName }}</span>
              <span v-else>{{ $t('@auditTrailEventsList/systemUserName') }}</span>
            </td>
            <grid-cell v-else :header="header" :row="props.item" :key="index"></grid-cell>
          </template>
        </tr>
      </template>
    </v-data-table>
  </section>
</template>

<script>
import GridCell from '../../components/GridCell';
import { serializeQuery, defaultDateTimeFormatter } from '../../utils';
import { getErrorMessage } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';
import HiddenPhoneNumber from '../../components/HiddenPhoneNumber';

const defaultHeaders = [
  {
    text: '@auditTrailEventsList/createdAtColumnHeader',
    value: 'createdAt',
    format: defaultDateTimeFormatter,
    sortable: false,
  },
  {
    text: '@auditTrailEventsList/messageColumnHeader',
    value: 'message',
    sortable: false,
    format: (value, row) => value || `Event of type "${row.type}"`,
  },
  {
    text: '@auditTrailEventsList/userColumnHeader',
    value: 'meta.employeeName',
    sortable: false,
  },
];

const employeeIdToName = {};

export default {
  components: { GridCell, ErrorsList, HiddenPhoneNumber },

  props: {
    filters: {
      type: Object,
      default: () => ({ contractId: 0, invoiceIds: [] }),
    },
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },
  },

  watch: {
    filters() {
      this.fetchData();
    },

    pagination() {
      this.fetchData();
    },
  },

  data() {
    return {
      lastQueryString: '',
      errorMessage: '',
      loading: false,
      items: [],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50, 100],
    };
  },

  computed: {
    translatedHeaders() {
      return Array.isArray(this.headers)
        ? this.headers.map((h) => ({
            ...h,
            text: this.$t(h.text),
          }))
        : [];
    },

    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const queryFilter = [
        { 'meta.contractId': this.filters.contractId },
        { 'meta.contractId': String(this.filters.contractId) },
        { 'meta.contractIds': { $all: [this.filters.contractId] } },
      ];

      if (this.filters.invoiceIds?.length) {
        queryFilter.push({ 'meta.invoiceId': { $in: this.filters.invoiceIds } });
      }

      if (this.filters.offerId) {
        queryFilter.push({ 'meta.offerId': this.filters.offerId });
      }

      const query = {
        query: JSON.stringify({
          $or: queryFilter,
        }),
        include: 'old new',
        skip,
        take,
        sortBy: '-registeredAt',
      };

      query.from = this.filters.from || new Date(-1);

      return query;
    },
  },

  methods: {
    async fetchData() {
      const { take } = this.query;
      const qs = serializeQuery(this.query);

      if (this.lastQueryString === qs || this.loading) {
        return;
      }

      try {
        this.loading = true;
        const { data } = await this.$http.get(`/api/employee/current/event?${qs}`);

        for (const event of data.data) {
          await this.augmentEmployeeName(event);
        }

        this.items = data.data || [];
        this.lastQueryString = qs;

        if (this.items.length === Number(take)) {
          this.pagination.totalItems = data.meta.total;
          this.pagination.skip = take;
        }

        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    async augmentEmployeeName(event) {
      if (!event.meta || !event.meta.employeeId) return;

      try {
        let name = employeeIdToName[event.meta.employeeId];

        if (!name) {
          const { data } = await this.$http.get(`/api/employees/${event.meta.employeeId}`);
          name = `${data.firstName} ${data.lastName}`;
          employeeIdToName[event.meta.employeeId] = name;
        }

        event.meta.employeeName = name;
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      }
    },

    getEventLink(event) {
      if (event.type === 'InsurancePolicyPdfGenerated') {
        return `/api/insurances/${event.meta.contractId}/pdf/policy`;
      } else if (event.type === 'InsuranceTerminationPdfGenerated') {
        return `/api/insurances/${event.meta.contractId}/pdf/termination`;
      } else if (event.type === 'InvoicePdfGenerated') {
        return `/api/invoices/${event.meta.invoiceId}/pdf`;
      } else if (event.meta.pdfId) {
        return `/api/invoices/pdf/${event.meta.pdfId}`;
      }
    },
  },

  sockets: {
    async insuranceSubscribe(event) {
      if (
        event.meta &&
        event.meta.contractId === this.filters.contractId &&
        this.pagination.page === 1
      ) {
        await this.augmentEmployeeName(event);

        this.items = [event, ...this.items];
      }
    },
  },
};
</script>

<style scoped>
code {
  width: 100%;
}
</style>
